import React, { useMemo } from 'react'
import {
  buildDayWindow,
  groupSlotsByStage,
  slotGeometry,
  formatTimeRange,
  formatDuration
} from '../../utils/scheduleUtils'

// Horizontal scale. 2.4px per minute makes a 60-minute set ~144px wide — room
// for an artist name without a three-stage day needing a mile of scroll.
const PX_PER_MINUTE = 2.4
const STAGE_COLUMN_PX = 132
const ROW_HEIGHT_PX = 64

/**
 * Festival-style schedule: stages down the left, time across the top.
 *
 * This is the layout every festival app uses because it answers the question
 * people actually have — "what is on right now, and what am I giving up to see
 * it?" A clash is visible as two blocks in the same vertical band rather than
 * something you have to be told about.
 *
 * The stage column is sticky so it stays readable while scrubbing through a
 * long day.
 */
const ScheduleGrid = ({ event, daySlots, stages, conflictKeys, onToggle, onCyclePriority, readOnly }) => {
  const window = useMemo(() => buildDayWindow(daySlots), [daySlots])
  const rows = useMemo(() => groupSlotsByStage(daySlots, stages), [daySlots, stages])

  if (!window) {
    return (
      <div className="text-center py-10 text-gray-400">
        No set times yet for this day.
      </div>
    )
  }

  const gridWidth = window.totalMinutes * PX_PER_MINUTE

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: `${STAGE_COLUMN_PX + gridWidth}px` }}>

        {/* Hour header */}
        <div className="flex sticky top-0 z-20">
          <div
            className="flex-shrink-0 bg-slate-900/80 backdrop-blur-sm border-b border-white/15"
            style={{ width: `${STAGE_COLUMN_PX}px` }}
          />
          <div className="relative flex-1 h-8 border-b border-white/15 bg-slate-900/80 backdrop-blur-sm">
            {window.hours.map(hour => (
              <div
                key={hour.minutes}
                className="absolute top-0 bottom-0 border-l border-white/10"
                style={{ left: `${((hour.minutes - window.startMinutes) / window.totalMinutes) * 100}%` }}
              >
                <span className="absolute left-1 top-1 text-[11px] text-gray-400 whitespace-nowrap tabular-nums">
                  {hour.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Stage rows */}
        {rows.map(row => (
          <div key={row.stage} className="flex border-b border-white/5">
            <div
              className={`flex-shrink-0 flex items-center px-3 py-2 sticky left-0 z-10 bg-slate-900/80 backdrop-blur-sm border-r border-white/10 ${
                row.isUnassigned ? 'text-gray-500 italic' : 'text-white'
              }`}
              style={{ width: `${STAGE_COLUMN_PX}px`, minHeight: `${ROW_HEIGHT_PX}px` }}
            >
              <span className="text-sm font-medium truncate" title={row.stage}>
                {row.stage}
              </span>
            </div>

            <div className="relative flex-1" style={{ minHeight: `${ROW_HEIGHT_PX}px` }}>
              {/* Hour gridlines, repeated per row so the alignment is visible
                  all the way down rather than only under the header. */}
              {window.hours.map(hour => (
                <div
                  key={hour.minutes}
                  className="absolute top-0 bottom-0 border-l border-white/5"
                  style={{ left: `${((hour.minutes - window.startMinutes) / window.totalMinutes) * 100}%` }}
                />
              ))}

              {row.slots.map(slot => {
                const { left, width } = slotGeometry(slot, window)
                const conflicted = conflictKeys.has(`${slot.key}|${slot.dayKey}`)
                const isMaybe = slot.picked && slot.priority === 'maybe'
                const duration = slot.effectiveEndMinutes - slot.startMinutes

                const tone = !slot.picked
                  ? 'bg-white/8 border-white/20 hover:bg-white/15'
                  : isMaybe
                    ? 'bg-amber-500/25 border-amber-400/60 hover:bg-amber-500/35'
                    : 'bg-purple-500/30 border-purple-400/70 hover:bg-purple-500/40'

                return (
                  <div
                    key={`${slot.key}-${slot.startTime}`}
                    className="absolute top-1.5 bottom-1.5"
                    style={{ left: `${left}%`, width: `${width}%` }}
                  >
                    <div
                      role={readOnly ? undefined : 'button'}
                      tabIndex={readOnly ? undefined : 0}
                      aria-pressed={readOnly ? undefined : slot.picked}
                      aria-label={`${slot.name}, ${formatTimeRange(slot.startTime, slot.endTime)}, ${row.stage}`}
                      onClick={readOnly ? undefined : () => onToggle(slot)}
                      onKeyDown={readOnly ? undefined : (e) => {
                        if (e.target !== e.currentTarget) return
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onToggle(slot)
                        }
                      }}
                      title={`${slot.name} · ${formatTimeRange(slot.startTime, slot.endTime)}`}
                      className={`h-full w-full rounded-lg border px-2 py-1 overflow-hidden transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 ${tone} ${
                        conflicted ? 'ring-2 ring-red-400/70' : ''
                      } ${readOnly ? '' : 'cursor-pointer'}`}
                    >
                      <div className="flex items-start justify-between gap-1">
                        <span className="text-xs font-semibold text-white truncate leading-tight">
                          {slot.name}
                        </span>
                        {slot.picked && !readOnly && (
                          <button
                            type="button"
                            onClick={(e) => {
                              // The block toggles the pick; without this the
                              // star would also un-pick what it just restyled.
                              e.stopPropagation()
                              onCyclePriority(slot)
                            }}
                            className="text-[11px] leading-none text-gray-200 hover:text-white flex-shrink-0"
                            title={isMaybe ? 'Mark as must-see' : 'Mark as maybe'}
                          >
                            {isMaybe ? '☆' : '★'}
                          </button>
                        )}
                      </div>

                      {/* Only worth the pixels on a block wide enough to read */}
                      {duration >= 45 && (
                        <div className="text-[10px] text-gray-300 truncate leading-tight">
                          {formatTimeRange(slot.startTime, slot.endTime)}
                        </div>
                      )}
                      {duration >= 90 && (
                        <div className="text-[10px] text-gray-400 truncate leading-tight">
                          {formatDuration(duration)}
                          {conflicted ? ' · ⚠ clash' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ScheduleGrid
